import type {
  ImageSlotId,
  Placement,
  RemovalStatus,
  SlotId,
  SourceImageMeta,
} from "./types";

export type Step = 1 | 2 | 3 | 4 | 5;

export interface SlotEdit {
  placement?: Placement;
  text?: string;
  value?: number;
}

export interface EditorState {
  step: Step;
  main: SourceImageMeta | null;
  subject: SourceImageMeta | null;
  removal: {
    status: RemovalStatus;
    // 除去済み画像のアセットID（成功時のみ）
    resultAssetId: string | null;
    // true なら除去なし（元画像のまま枠に配置）
    useOriginal: boolean;
  };
  templateId: string | null;
  edits: Partial<Record<SlotId, SlotEdit>>;
}

export const initialState: EditorState = {
  step: 1,
  main: null,
  subject: null,
  removal: { status: "idle", resultAssetId: null, useOriginal: false },
  templateId: null,
  edits: {},
};

export type EditorAction =
  | { type: "imageLoaded"; slot: ImageSlotId; image: SourceImageMeta }
  | { type: "removalStatusChanged"; status: RemovalStatus; resultAssetId?: string }
  | { type: "useOriginalChosen"; useOriginal: boolean }
  | { type: "templateSelected"; templateId: string }
  | { type: "slotEdited"; slot: SlotId; edit: SlotEdit }
  | { type: "stepChanged"; step: Step };

// 編集状態の引き継ぎ規則（機能設計書 1.3.4）:
// - テンプレート再選択: テキスト・数値・切り抜き結果は引き継ぎ、配置（placement）のみ
//   テンプレート初期値へリセットする（テンプレートの試し比べを入力し直しなしで行うため)
// - 素材差し替え: 差し替えた画像の切り抜き・配置のみ初期化し、他は維持する
export function reduce(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case "imageLoaded": {
      if (action.slot === "main") {
        const edits = { ...state.edits };
        delete edits.main;
        return { ...state, main: action.image, edits };
      }
      const edits = { ...state.edits };
      delete edits.facecam;
      return {
        ...state,
        subject: action.image,
        removal: { status: "idle", resultAssetId: null, useOriginal: false },
        edits,
      };
    }
    case "removalStatusChanged":
      return {
        ...state,
        removal: {
          ...state.removal,
          status: action.status,
          resultAssetId:
            action.status === "done" ? (action.resultAssetId ?? null) : state.removal.resultAssetId,
        },
      };
    case "useOriginalChosen":
      return { ...state, removal: { ...state.removal, useOriginal: action.useOriginal } };
    case "templateSelected": {
      const edits: EditorState["edits"] = {};
      for (const [slot, edit] of Object.entries(state.edits) as [SlotId, SlotEdit][]) {
        const kept: SlotEdit = {};
        if (edit.text !== undefined) kept.text = edit.text;
        if (edit.value !== undefined) kept.value = edit.value;
        if (Object.keys(kept).length > 0) edits[slot] = kept;
      }
      return { ...state, templateId: action.templateId, edits };
    }
    case "slotEdited":
      return {
        ...state,
        edits: {
          ...state.edits,
          [action.slot]: { ...state.edits[action.slot], ...action.edit },
        },
      };
    case "stepChanged":
      return { ...state, step: action.step };
  }
}

// 各ステップへ進入できる条件。素材が揃うまで先へ進めない（機能設計書 1.3.1）
export function canEnterStep(state: EditorState, step: Step): boolean {
  switch (step) {
    case 1:
      return true;
    case 2:
      return state.main !== null && state.subject !== null;
    case 3:
      // 切り抜きは「除去なしで先に進む」を常時許容するため、素材が揃っていれば進める
      return canEnterStep(state, 2);
    case 4:
      return canEnterStep(state, 3) && state.templateId !== null;
    case 5:
      return canEnterStep(state, 4);
  }
}

// フェイスカム枠に使う被写体アセット。除去済みがあり「除去なし」を選んでいなければ除去済み
export function activeSubjectAssetId(state: EditorState): string | null {
  if (state.subject === null) return null;
  if (!state.removal.useOriginal && state.removal.resultAssetId !== null) {
    return state.removal.resultAssetId;
  }
  return state.subject.assetId;
}

// undo / redo（誤操作からの復帰手段。対象は編集操作のみ）
export interface History {
  past: EditorState[];
  future: EditorState[];
}

export const emptyHistory: History = { past: [], future: [] };

const UNDOABLE: ReadonlySet<EditorAction["type"]> = new Set(["slotEdited"]);

export function applyWithHistory(
  state: EditorState,
  history: History,
  action: EditorAction,
): { state: EditorState; history: History } {
  const next = reduce(state, action);
  if (!UNDOABLE.has(action.type)) {
    return { state: next, history };
  }
  return {
    state: next,
    history: { past: [...history.past, state], future: [] },
  };
}

export function undo(
  state: EditorState,
  history: History,
): { state: EditorState; history: History } {
  const prev = history.past[history.past.length - 1];
  if (prev === undefined) return { state, history };
  return {
    state: prev,
    history: { past: history.past.slice(0, -1), future: [state, ...history.future] },
  };
}

export function redo(
  state: EditorState,
  history: History,
): { state: EditorState; history: History } {
  const next = history.future[0];
  if (next === undefined) return { state, history };
  return {
    state: next,
    history: { past: [...history.past, state], future: history.future.slice(1) },
  };
}
